import { useRSO } from "@/api/services/admin/rso";
import { createRSOUserApi, updateRSOUserApi, resendInvitationApi, resetPasswordApi } from "@/api/services/admin/rso/user";
import { useEnum } from "@/api/services/global/enum";
import { Form } from "@/components/elements/Form";
import { FormField } from "@/components/elements/FormField";
import { Input } from "@/components/elements/Input";
import { Select } from "@/components/elements/Select";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { updatePartnerUserSchema } from "@/features/rso/schema/partnerUserSchema";
import { TUpdatePartnerUserForm } from "@/features/rso/types/updatePartnerUserForm";
import { useToast } from "@/hooks/useToast";
import { AppRoutes } from "@/routes/config";
import { setErrorForm, showError } from "@/utils/error";
import { getDynamicRoute } from "@/utils/url";
import { yupResolver } from "@hookform/resolvers/yup";
import { Grid, Stack, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { forwardRef, useImperativeHandle, useState, useEffect } from "react";
import { MenuNavigate } from "@/components/elements/MenuNavigate";
import { InnerBox } from "@/components/elements/InnerBox";
import { Button } from "@/components/elements/Button";
import { FooterContainer } from "@/components/commons/FooterContainer";
import { IconSend } from "@/assets/icons";
import { text, background } from "@/theme/colors";
import { getAuthStatusLabel } from "@/utils/authStatus";
import { USER_ACTIVE_STATUS, USER_AUTH_STATUS } from "@/constants/enum";
import { IconSave } from "@/assets/icons";

const initCreateForm: Partial<TUpdatePartnerUserForm> = {
  fullName: "",
  email: "",
};

type TUpdatePartnerUserFormProps = {
  previousData?: Partial<TUpdatePartnerUserForm>;
  userData?: {
    cognitoStatus?: number;
    activeStatus?: string;
  };
  /** Bypass navigation blocker (e.g., after delete success) */
  forceDisableBlocker?: boolean;
};

export type UpdatePartnerUserFormRef = {
  submit: () => Promise<void>;
};

export const UpdatePartnerUserForm = forwardRef<UpdatePartnerUserFormRef, TUpdatePartnerUserFormProps>(
  ({ previousData, userData, forceDisableBlocker = false }, ref) => {
    const { data: activeStatuses } = useEnum("EUserActiveStatus");
    const { data: rsoResult } = useRSO();
    const rsoName = rsoResult?.data?.name;
    const toast = useToast();
    const { id, userId } = useParams();
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const methods = useForm<TUpdatePartnerUserForm>({
      defaultValues: previousData ?? initCreateForm,
      resolver: yupResolver(updatePartnerUserSchema),
      mode: "all",
      reValidateMode: "onBlur",
    });
    const { control, watch, trigger } = methods;
    const isUser = watch("isUser");
    const [loading, setLoading] = useState(false);

    // Trigger email validation when isUser changes
    useEffect(() => {
      if (isUser !== undefined && isUser !== null) {
        trigger("email");
      }
    }, [isUser, trigger]);

    const getUserAuthStatusLabel = () => {
      let authStatus = USER_AUTH_STATUS.EMAIL_SENT;
      if (userData?.activeStatus === USER_ACTIVE_STATUS.INACTIVE) {
        authStatus = USER_AUTH_STATUS.USER_SUSPENDED;
      }

      if (userData?.activeStatus === USER_ACTIVE_STATUS.ACTIVE) {
        if (userData?.cognitoStatus === 1) {
          authStatus = USER_AUTH_STATUS.PASSWORD_SET;
        }
      }

      return getAuthStatusLabel(authStatus);
    };

    const onSubmit = async (data: TUpdatePartnerUserForm) => {
      setLoading(true);
      try {
        if (!id) throw new Error();
        if (userId) {
          const rs = await updateRSOUserApi(id, userId, data);
          toast.success(rs.message ?? API_SUCCESS_MESSAGE);
          queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.RSO.FETCH_DETAIL, id] });
          queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.RSO.FETCH_USER_DETAIL, id, userId] });
        } else {
          const payload = { ...data, isUser: Boolean(data.isUser) };
          const rs = await createRSOUserApi(id, payload);
          toast.success(rs.message ?? API_SUCCESS_MESSAGE);
        }
        navigate(getDynamicRoute(AppRoutes.rsoDetail, { id }));
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.RSO.FETCH_USER_LIST] });
      } catch (error) {
        showError(error);
        setErrorForm(methods, error);
      }
      setLoading(false);
    };

    useImperativeHandle(ref, () => ({
      submit: async () => {
        await methods.handleSubmit(onSubmit)();
      },
    }));

    const handleReinvite = async () => {
      try {
        if (!id) throw new Error();
        if (!userId) throw new Error();
        const rs = await resendInvitationApi(id, userId);
        toast.success(rs.message ?? API_SUCCESS_MESSAGE);
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.RSO.FETCH_USER_DETAIL, id, userId] });
      } catch (error) {
        showError(error);
      }
    };

    const handleResetPassword = async () => {
      try {
        if (!id) throw new Error();
        if (!userId) throw new Error();

        if (userData?.cognitoStatus === 0 || userData?.activeStatus === USER_ACTIVE_STATUS.INACTIVE) {
          toast.error("パスワードが未登録で、ユーザーが停止中のため、パスワードリセットメールを送信できません。");
          return;
        }

        const rs = await resetPasswordApi(id, userId);
        toast.success(rs.message ?? API_SUCCESS_MESSAGE);
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.RSO.FETCH_USER_DETAIL, id, userId] });
      } catch (error) {
        showError(error);
      }
    };

    return (
      <Form methods={methods} onSubmit={onSubmit} schema={updatePartnerUserSchema} enableBlocker={!forceDisableBlocker}>
        <MenuNavigate
          contentWidth="900px"
          contents={[
            {
              title: "ユーザーの状態",
              content: (
                <InnerBox title="ユーザーの状態">
                  <Grid container columnSpacing={3} rowSpacing={2} alignItems="flex-end">
                    <Grid item xs={5}>
                      {userId && (
                        <FormField control={control} name="activeStatus">
                          <Select options={activeStatuses} placeholder="使用中 / 停止中" />
                        </FormField>
                      )}
                    </Grid>
                    <Grid item xs={4}>
                      <Stack gap={0.5}>
                        <Typography variant="cap12" color={text.secondary}>
                          認証ステータス
                        </Typography>
                        <Typography variant="body14" color={text.primary}>
                          {getUserAuthStatusLabel()}
                        </Typography>
                      </Stack>
                    </Grid>
                    <Grid item xs={3}>
                      <Button
                        size="sm-round"
                        variant="secondary"
                        onClick={handleResetPassword}
                        disabled={userData?.cognitoStatus === 0 || userData?.activeStatus === USER_ACTIVE_STATUS.INACTIVE}
                        sx={{
                          width: "100%",
                          backgroundColor: `${background.disable}`,
                          color: text.tertiary,
                          "&.Mui-disabled": {
                            backgroundColor: `${background.disable} !important`,
                            color: `${text.tertiary} !important`,
                          },
                        }}
                      >
                        パスワードリセット
                      </Button>
                    </Grid>
                  </Grid>
                </InnerBox>
              ),
            },
            {
              title: "基本情報",
              content: (
                <InnerBox title="基本情報">
                  <Grid container columnSpacing={3} rowSpacing={2}>
                    <Grid item xs={6}>
                      <FormField label="顧客名">
                        <Input value={rsoName || ""} disabled />
                      </FormField>
                    </Grid>
                    <Grid item xs={6} />
                    <Grid item xs={6}>
                      <FormField control={control} name="fullName">
                        <Input />
                      </FormField>
                    </Grid>
                    <Grid item xs={6}>
                      <FormField control={control} name="email">
                        <Input />
                      </FormField>
                    </Grid>
                    <Grid item xs={6}>
                      <FormField control={control} name="isUser">
                        <Select
                          disabled={!!(id && userId)}
                          placeholder="顧客ユーザー / irohana担当者"
                          options={[
                            { label: "irohana担当者", value: 0 },
                            { label: "顧客ユーザー", value: 1 },
                          ]}
                        />
                      </FormField>
                    </Grid>
                  </Grid>
                </InnerBox>
              ),
            },
          ]}
          contentAffix={
            <FooterContainer>
              <Stack gap={1} direction="row" justifyContent="space-between" alignItems="center">
                <Button size="sm-round" variant="secondary" onClick={() => navigate(getDynamicRoute(AppRoutes.rsoDetail, { id }))}>
                  戻る
                </Button>
                <Stack direction="row" gap={3}>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleReinvite}
                    disabled={userData?.cognitoStatus === 1}
                    startIcon={<IconSend />}
                    sx={{
                      "& .MuiButton-startIcon svg path": {
                        fill: "currentColor",
                      },
                    }}
                  >
                    再招待
                  </Button>
                  <Button
                    disabled={loading}
                    size="sm"
                    type="submit"
                    startIcon={<IconSave />}
                    sx={{
                      "& .MuiButton-startIcon svg path": {
                        fill: "currentColor",
                      },
                    }}
                  >
                    保存
                  </Button>
                </Stack>
              </Stack>
            </FooterContainer>
          }
        />
      </Form>
    );
  },
);
